import { Text } from '@chakra-ui/react';
import { TFunction } from 'i18next';
import { createColumnHelper } from '@tanstack/react-table';
import {
  ProductApproval,
  ProductApprovalState,
} from '@texas/api/endpoints/productApprovalsApi';
import { formatDate } from '@texas/utils/helpers/dateHelper';
import { Locale } from '@texas/i18n/types';
import { Flex } from '@chakra-ui/react';
import { PaMenu } from './PaMenu';
import {
  convertToEnum,
  ensureEnumNumber,
} from '@texas/utils/helpers/enumHelpers';
import { ProductApprovalHistoryModal } from './ProductApprovalHistoryModal';
import { MirroringTag } from '@texas/components/shared/suppliers/MirroringTag';
import { MirrorTag } from '@texas/components/shared/suppliers/MirrorTag';
import { SampleState } from '@texas/components/shared/samples/SampleState';
import { SampleLifecycle } from '@texas/components/shared/samples/types';
import { CompositionGroupTitle } from '@texas/components/shared/compositionGroup/CompositionGroupTitle';

export const productApprovalColumns = (
  isDarkMode: boolean,
  t: TFunction,
  onStateChange: (id?: number) => void,
) => {
  const columnHelper = createColumnHelper<ProductApproval>();
  const columns = [
    columnHelper.accessor('formatNumber', {
      cell: (info) => info.getValue(),
      header: t('productApproval.number'),
      enableSorting: true,
      meta: { toggleable: true, defaultVisible: true },
    }),
    columnHelper.accessor('branchIdentifier', {
      cell: (info) => info.getValue(),
      header: t('general.branch'),
      enableSorting: true,
      maxSize: 100,
      meta: { toggleable: true, defaultVisible: true },
    }),
    columnHelper.accessor('supplierName', {
      cell: (info) => (
        <Flex direction="column" gap={1}>
          <Text whiteSpace="normal">{info.getValue()}</Text>
          {info.row.original.mainSupplier && (
            <MirrorTag mainSupplier={info.row.original.mainSupplier} />
          )}
          {info.row.original.followers &&
            info.row.original.followers.length > 0 && (
              <MirroringTag
                showCount={true}
                followers={info.row.original.followers}
              />
            )}
        </Flex>
      ),
      header: t('suppliers.supplier'),
      enableSorting: true,
      meta: { toggleable: true, defaultVisible: true },
    }),
    columnHelper.accessor('compositionGroupVersion', {
      cell: (info) =>
        t('compositionGroup.version.versionNr', { nr: info.getValue() }),
      header: t('compositionGroup.version.supplierVersion'),
      enableSorting: true,
      meta: { toggleable: true, defaultVisible: true },
    }),
    columnHelper.accessor('compositionGroupBasedOnOption', {
      cell: (info) => (
        <CompositionGroupTitle
          version={info.row.original.compositionGroupBasedOnOption}
          note={info.row.original.compositionGroupBasedOnOptionNote}
          type="option"
          variant="unset"
        />
      ),
      header: t('composition.basedOnOption'),
      enableSorting: true,
      meta: { toggleable: true, defaultVisible: true },
    }),
    columnHelper.accessor('created', {
      cell: (info) => formatDate(Locale.En, info.getValue()),
      header: t('article.created'),
      enableSorting: true,
      meta: { toggleable: true, defaultVisible: true },
    }),
    columnHelper.accessor('sample', {
      cell: (info) => {
        const sample = info.getValue();
        const lifecycle = sample
          ? ensureEnumNumber(SampleLifecycle, sample.lifecycle)
          : SampleLifecycle.Nothing;
        return (
          <SampleState
            lifecycle={lifecycle}
            sampleReadyDate={sample?.sampleReadyDate ?? null}
          />
        );
      },
      header: t('sample.sample'),
      enableSorting: false,
      maxSize: 100,
      meta: {
        toggleable: true,
        defaultVisible: true,
      },
    }),
    columnHelper.accessor('state', {
      cell: (info) => (
        <Flex gap={2} alignItems="center" justifyContent="space-between">
          <Flex direction="column">
            <Text
              w="16"
              color={stateColor(
                convertToEnum(ProductApprovalState, info.getValue()),
                isDarkMode,
              )}
            >
              {info.getValue()}
            </Text>
            {info.row.original.handledDate && (
              <Text fontSize="sm">
                {formatDate(Locale.En, info.row.original.handledDate)}
              </Text>
            )}
          </Flex>
          <Flex gap={1}>
            {!info.row.original.mainSupplier && (
              <PaMenu
                onChanged={onStateChange}
                state={convertToEnum(
                  ProductApprovalState,
                  info.row.original.state,
                )}
                isMatrix={info.row.original.matrixNodeXId !== null}
                id={info.row.original.id}
                branchId={info.row.original.branchId}
                variantId={info.row.original.variantId}
                linkedVariantId={info.row.original.linkedVariantId}
                compositionGroupId={info.row.original.compositionGroupId}
              />
            )}
            <ProductApprovalHistoryModal
              productApprovalId={info.row.original.id}
              number={info.row.original.formatNumber}
            />
          </Flex>
        </Flex>
      ),
      header: t('general.state'),
      enableSorting: true,
      meta: { toggleable: true, defaultVisible: true },
    }),
  ];

  return columns;
};

function stateColor(state: ProductApprovalState, isDarkMode: boolean) {
  switch (state) {
    case ProductApprovalState.Pending:
      return isDarkMode ? 'gray.300' : 'gray.500';
    case ProductApprovalState.Cancelled:
      return isDarkMode ? 'gray.300' : 'gray.500';
    case ProductApprovalState.OnHold:
      return isDarkMode ? 'gray.300' : 'gray.500';
    case ProductApprovalState.Accepted:
      return 'inherit';
    case ProductApprovalState.Rejected:
      return isDarkMode ? 'red.300' : 'red.500';
  }
}
